import axios from "axios";
import { useEffect, useState, useContext } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { FiFilter, FiHeart, FiShoppingCart, FiTrash2 } from "react-icons/fi";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "../../assets/css/customer/customerDashboard/wishlist/Wishlist.css";
import ProductCard from "../../components/customer/cards/productCard/ProductCard";
import Button from "../../components/general/button/Button";
import { WishlistPageSkeleton } from "../../components/general/skeleton/SkeletonBlock";
import CustomerMasterLayout from "../../layouts/CustomerMasterLayout";
import MyContext from "../../context/MyContext";

const Wishlist = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, authToken, setCartProducts } = useContext(MyContext);

  const [loading, setLoading] = useState(true);
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("default");

  /* ══════════
     Categories — wishlist ke apne products se nikalti hain (hardcoded nahi)
  ══════════ */
  const categories = [
    "All",
    ...new Set(
      wishlistProducts
        .map((item) => item.kmproduct?.kmsubcategory?.kmcategory?.categoryname)
        .filter(Boolean),
    ),
  ];
  // agar selected category ke saare products remove ho jayen to wapas "All"
  const activeCategory = categories.includes(selectedCategory) ? selectedCategory : "All";

  /* ══════════
     Filter + Sort — sirf jo screen pe dikhana hai
  ══════════ */
  const visibleProducts = wishlistProducts
    .filter(
      (item) =>
        activeCategory === "All" ||
        item.kmproduct?.kmsubcategory?.kmcategory?.categoryname === activeCategory,
    )
    .sort((a, b) => {
      if (sortBy === "price-low") return Number(a.kmproduct?.price) - Number(b.kmproduct?.price);
      if (sortBy === "price-high") return Number(b.kmproduct?.price) - Number(a.kmproduct?.price);
      return 0;
    });

  /* ══════════
     viewAllWishlistProducts - API se data lao
  ══════════ */
  const viewAllWishlistProducts = () => {
    if (!authToken) {
      setLoading(false);
      navigate("/customer/login");
      return;
    }

    setLoading(true);

    axios
      .get(`${baseUrl}/customer/wishlist/viewAllWishlistProducts`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.error || response.data.success === false) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error || response.data.message,
          });
        } else {
          setWishlistProducts(response.data.wishlistProducts || []);
          setWishlistCount(response.data.wishlistProducts?.length || 0);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        // 401 = token expire, 403 = token kisi aur role (seller/admin) ka hai
        if ([401, 403].includes(error.response?.status)) {
          navigate("/customer/login");
          return;
        }
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load wishlist" });
      });
  };

  /* ══════════════════════════════════
     removeProductFromWishlist - Heart button se call hoga
  ══════════ */
  const removeProductFromWishlist = (idToRemove) => {
    const formData = new FormData();
    formData.append("id", idToRemove);

    axios
      .post(`${baseUrl}/customer/wishlist/removeProductFromWishlist`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.error || response.data.success === false) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error || response.data.message,
          });
        } else {
          Swal.fire({ icon: "success", title: "Removed!", text: "Product removed", timer: 1200, showConfirmButton: false });
          // poori list dobara load karne ki bajaye sirf wahi item hata do
          const remaining = wishlistProducts.filter((item) => item.id !== idToRemove);
          setWishlistProducts(remaining);
          setWishlistCount(remaining.length);
        }
      })
      .catch((error) => {
        if ([401, 403].includes(error.response?.status)) {
          navigate("/customer/login");
          return;
        }
        Swal.fire({ icon: "error", title: "Error", text: "Failed to remove" });
      });
  };

  /* ══════════
     viewCartForContext - cart ka latest data context me daalo
     (taake ProductCard pe "in cart" sahi dikhe)
  ══════════ */
  const viewCartForContext = () => {
    axios
      .post(`${baseUrl}/customer/cart/viewAllCartProducts`, new FormData(), {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (!response.data.error && response.data.success) {
          setCartProducts(response.data.cartProducts);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  /* ══════════
     addAllToCart - Saari wishlist cart me daalo
     Quantity = product ki min_order_quantity (warna 1).
     Backend success:false bhejta hai (e.g. dusra seller / out of stock),
     is liye har response check hota hai — sirf request chalne ko success nahi maante.
  ══════════ */
  const addAllToCart = () => {
    if (!authToken || wishlistProducts.length === 0) return;

    setLoading(true);
    const total = wishlistProducts.length;
    let completed = 0;
    let addedCount = 0;
    let sessionExpired = false;
    const failedMessages = [];

    const checkAllDone = () => {
      completed++;
      if (completed < total) return;
      setLoading(false);
      if (sessionExpired) {
        navigate("/customer/login");
        return;
      }
      viewCartForContext();
      // Backend ab cart mein add hote hi wishlist se bhi hata deta hai —
      // list ko refresh kar dein taake add hue items yahan se turant hat jayen
      viewAllWishlistProducts();

      if (failedMessages.length === 0) {
        Swal.fire({
          icon: "success",
          title: "Added to Cart!",
          text: "All items added to cart",
          confirmButtonColor: "#097d43",
        });
      } else {
        Swal.fire({
          icon: addedCount > 0 ? "warning" : "error",
          title: addedCount > 0 ? "Some items not added" : "Could not add items",
          text: `${addedCount} added, ${failedMessages.length} not added. ${failedMessages[0]}`,
          confirmButtonColor: "#097d43",
        });
      }
    };

    wishlistProducts.map((item) => {
      const product = item.kmproduct;
      const quantity = product?.min_order_quantity || 1;

      const formData = new FormData();
      formData.append("kmproduct_id", product?.id);
      formData.append("amountPerPiece", product?.price);
      formData.append("quantity", quantity);
      formData.append("total_price", product?.price * quantity);

      axios
        .post(`${baseUrl}/customer/cart/addProductToCart`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken}`,
          },
        })
        .then((response) => {
          if (response.data.error || response.data.success === false) {
            failedMessages.push(`${product?.name}: ${response.data.error || response.data.message}`);
          } else {
            addedCount++;
          }
          checkAllDone();
        })
        .catch((error) => {
          if ([401, 403].includes(error.response?.status)) {
            sessionExpired = true;
          }
          failedMessages.push(`${product?.name}: request failed`);
          checkAllDone();
        });

      return null;
    });
  };

  /* ══════════
     clearAll - Sab clear with confirmation
  ══════════ */
  const clearAll = () => {
    Swal.fire({
      title: "Clear Wishlist?",
      text: "Remove all items?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#097d43",
      confirmButtonText: "Yes, clear all!",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setLoading(true);
      const total = wishlistProducts.length;
      let completed = 0;
      let hasError = false;
      let sessionExpired = false;

      const checkAllDone = () => {
        completed++;
        if (completed < total) return;
        if (sessionExpired) {
          setLoading(false);
          navigate("/customer/login");
          return;
        }
        if (hasError) {
          Swal.fire({ icon: "error", title: "Error", text: "Could not clear wishlist" });
          viewAllWishlistProducts(); // jo reh gaya wo dobara load karo
        } else {
          setLoading(false);
          Swal.fire({ icon: "success", title: "Cleared!", text: "Wishlist cleared", confirmButtonColor: "#097d43" });
          setWishlistProducts([]);
          setWishlistCount(0);
        }
      };

      wishlistProducts.map((item) => {
        const formData = new FormData();
        formData.append("id", item.id);
        axios
          .post(`${baseUrl}/customer/wishlist/removeProductFromWishlist`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${authToken}`,
            },
          })
          .then((response) => {
            if (response.data.success === false) hasError = true;
            checkAllDone();
          })
          .catch((error) => {
            hasError = true;
            if ([401, 403].includes(error.response?.status)) {
              sessionExpired = true;
            }
            checkAllDone();
          });

        return null;
      });
    });
  };

  useEffect(() => {
    viewAllWishlistProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  if (loading)
    return (
      <CustomerMasterLayout>
        <WishlistPageSkeleton />
      </CustomerMasterLayout>
    );

  return (
    <CustomerMasterLayout>
      <Container fluid className="wishlist-container">
        {/* Header */}
        <div className="wishlist-header">
          <div className="wishlist-header-left">
            <div className="wishlist-icon-wrapper">
              <FiHeart size={28} />
            </div>
            <div>
              <h2 className="wishlist-page-title">My Wishlist</h2>
              <p className="wishlist-subtitle">
                {wishlistCount} {wishlistCount === 1 ? "item" : "items"} saved
              </p>
            </div>
          </div>

          {wishlistProducts.length > 0 && (
            <div className="wishlist-header-actions">
              <Button variant="primary" icon={<FiShoppingCart size={16} />} onClick={() => addAllToCart()}>
                Add All to Cart
              </Button>
              <Button variant="secondary" icon={<FiTrash2 size={16} />} className="btn-danger-custom" onClick={() => clearAll()}>
                Clear All
              </Button>
            </div>
          )}
        </div>

        {/* Products */}
        {wishlistProducts.length > 0 ? (
          <>
            {/* Toolbar */}
            <div className="wishlist-toolbar">
              <div className="toolbar-left">
                <FiFilter size={18} />
                <span className="toolbar-label">Filter by:</span>
                <div className="category-filter">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      className={`category-filter-btn ${activeCategory === cat ? "active" : ""}`}
                      onClick={() => setSelectedCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
              <div className="toolbar-right">
                <span className="toolbar-label">Sort by:</span>
                <select className="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="default">Default</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </div>

            {/* Product Grid */}
            <Row className="g-4 wishlist-products-grid">
              {visibleProducts.map((item, index) => (
                <Col key={item.id} xs={12} sm={6} md={4} lg={3}>
                  <ProductCard
                    product={item.kmproduct}
                    index={index}
                    baseUrlForFiles={baseUrlForFiles}
                    isWishlisted={true}
                    onRemoveFromWishlist={() => removeProductFromWishlist(item.id)}
                  />
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <div className="empty-wishlist">
            <div className="empty-wishlist-icon">
              <FiHeart size={80} />
            </div>
            <h3 className="empty-wishlist-title">Your Wishlist is Empty</h3>
            <p className="empty-wishlist-text">Start adding products you love!</p>
            <Button variant="primary" icon={<FiShoppingCart size={16} />} onClick={() => navigate("/products")}>
              Continue Shopping
            </Button>
          </div>
        )}
      </Container>
    </CustomerMasterLayout>
  );
};

export default Wishlist;
